import { type ComputedRef, type MaybeRef, computed, unref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import type { IdentityChild, IdentityPerson, PersonReferentials } from '~/interfaces/opportunities/identity/main';
import type { OpportunityConsents } from '~/interfaces/opportunities/identity/consents';
import { getOpportunityChildren, getOpportunityPersons, getPersonReferentials } from '~/api/opportunities/identity';
import { getOpportunityConsents } from '~/api/opportunities/consents';
import { identityKeys } from '~/composables/opportunities/identity/identityKeys';

type EnabledRef = MaybeRef<boolean | undefined>;

interface UseOpportunityPersonsQueryOptions {
  enabled?: EnabledRef;
}

interface UseOpportunityChildrenQueryOptions {
  enabled?: EnabledRef;
}

interface UsePersonReferentialsQueryOptions {
  enabled?: EnabledRef;
}

interface UseOpportunityConsentsQueryOptions {
  enabled?: EnabledRef;
}

export interface UseOpportunityPersonsQueryResult {
  query: ReturnType<typeof useQuery<IdentityPerson[]>>;
  persons: ComputedRef<IdentityPerson[]>;
}

export interface UseOpportunityChildrenQueryResult {
  query: ReturnType<typeof useQuery<IdentityChild[]>>;
  children: ComputedRef<IdentityChild[]>;
}

export interface UsePersonReferentialsQueryResult {
  query: ReturnType<typeof useQuery<PersonReferentials | null>>;
  referentials: ComputedRef<PersonReferentials | null>;
}

export interface UseOpportunityConsentsQueryResult {
  query: ReturnType<typeof useQuery<OpportunityConsents | null>>;
  consents: ComputedRef<OpportunityConsents | null>;
}

export function useOpportunityPersonsQuery (
  opportunityHashId: MaybeRef<string | null | undefined>,
  options: UseOpportunityPersonsQueryOptions = {},
): UseOpportunityPersonsQueryResult {
  const enabled = computed<boolean>(() => {
    const requested = options.enabled === undefined ? true : Boolean(unref(options.enabled));
    return requested && Boolean(unref(opportunityHashId));
  });

  const queryKey = computed(() => identityKeys.persons(unref(opportunityHashId) ?? null));

  const query = useQuery<IdentityPerson[]>({
    queryKey,
    queryFn: () => {
      const resolvedId = unref(opportunityHashId);
      if (!resolvedId) { return []; }
      return getOpportunityPersons(resolvedId);
    },
    enabled,
    staleTime: 0,
    gcTime: 15 * 60 * 1000,
  });

  const persons = computed<IdentityPerson[]>(() => query.data?.value ?? []);

  return { query, persons };
}

export function useOpportunityChildrenQuery (
  opportunityHashId: MaybeRef<string | null | undefined>,
  options: UseOpportunityChildrenQueryOptions = {},
): UseOpportunityChildrenQueryResult {
  const enabled = computed<boolean>(() => {
    const requested = options.enabled === undefined ? true : Boolean(unref(options.enabled));
    return requested && Boolean(unref(opportunityHashId));
  });

  const queryKey = computed(() => identityKeys.children(unref(opportunityHashId) ?? null));

  const query = useQuery<IdentityChild[]>({
    queryKey,
    queryFn: () => {
      const resolvedId = unref(opportunityHashId);
      if (!resolvedId) { return []; }
      return getOpportunityChildren(resolvedId);
    },
    enabled,
    staleTime: 0,
    gcTime: 15 * 60 * 1000,
  });

  const children = computed<IdentityChild[]>(() => query.data?.value ?? []);

  return { query, children };
}

export function usePersonReferentialsQuery (
  options: UsePersonReferentialsQueryOptions = {},
): UsePersonReferentialsQueryResult {
  const enabled = computed<boolean>(() => options.enabled === undefined ? true : Boolean(unref(options.enabled)));

  const queryKey = computed(() => identityKeys.referentials());

  const query = useQuery<PersonReferentials | null>({
    queryKey,
    queryFn: () => getPersonReferentials(),
    enabled,
    staleTime: 0,
    gcTime: 15 * 60 * 1000,
  });

  const referentials = computed<PersonReferentials | null>(() => query.data?.value ?? null);

  return { query, referentials };
}

export function useOpportunityConsentsQuery (
  opportunityHashId: MaybeRef<string | null | undefined>,
  opportunityReqId: MaybeRef<number | null | undefined>,
  options: UseOpportunityConsentsQueryOptions = {},
): UseOpportunityConsentsQueryResult {
  const enabled = computed<boolean>(() => {
    const requested = options.enabled === undefined ? true : Boolean(unref(options.enabled));
    return requested && Boolean(unref(opportunityReqId));
  });

  const queryKey = computed(() => identityKeys.consents(unref(opportunityHashId) ?? null));

  const query = useQuery<OpportunityConsents | null>({
    queryKey,
    queryFn: () => {
      const resolvedId = unref(opportunityReqId);
      if (!resolvedId) { return null; }
      return getOpportunityConsents(resolvedId);
    },
    enabled,
    staleTime: 0,
    gcTime: 15 * 60 * 1000,
  });

  const consents = computed<OpportunityConsents | null>(() => query.data?.value ?? null);

  return { query, consents };
}
